import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "../context/LanguageContext";

function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const duration = 1800;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {display}
      {suffix}
    </span>
  );
}

export default function Metrics() {
  const { t } = useLang();

  return (
    <section
      id="capabilities"
      data-testid="metrics-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0B] border-y border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.8)_0,transparent_60%)] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="overline">{t.metrics.eyebrow}</span>
        </div>

        <h2 className="font-display font-bold text-[clamp(2rem,4vw,3.5rem)] text-white tracking-tight leading-[1.05] max-w-[22ch] mb-16">
          {t.metrics.title}
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-white/10">
          {t.metrics.items.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 * i, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`metric-${i}`}
              className={`py-10 lg:py-14 px-4 lg:px-8 ${
                i < 3 ? "lg:border-r border-white/10" : ""
              } ${i % 2 === 0 ? "border-r lg:border-r border-white/10" : ""} ${
                i < 2 ? "border-b lg:border-b-0 border-white/10" : ""
              }`}
            >
              <div className="font-display font-black text-[clamp(3rem,7vw,6rem)] leading-none gold-gradient-text tracking-tighter">
                <Counter value={m.value} suffix={m.suffix} />
              </div>
              <div className="mt-4 text-white text-sm lg:text-base font-semibold tracking-tight">
                {m.label}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#8B939C]">
                {m.sub}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
